/**
 * @group Components
 * 
 * @summary
 * <blockquote>Silent error boundary that logs React errors without displaying UI.</blockquote>
 * 
 * @remarks
 * This error boundary catches React render errors, logs them using Logger (which respects ResourceConfig),
 * and continues rendering children. It does NOT display any error UI - it's purely for error logging.
 * 
 * Errors are logged to whatever transport is configured (Sentry, Console, Axiom, etc.),
 * making it transport-agnostic and easy to switch between logging solutions.
 * 
 * @example
 * ```typescript
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
'use client';

import React, { Component, ReactNode } from 'react';
import { Logger } from '@shared/logger';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error using Logger (respects ResourceConfig - works with any transport)
    // Fire-and-forget: don't await to avoid blocking error UI rendering
    Logger.error('React Error Boundary - Component render error', {
      errorType: 'reactErrorBoundary',
      errorMessage: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    }, error);

    // Reset error state after logging to continue rendering children
    // This makes the ErrorBoundary a silent wrapper that only logs errors
    this.setState({ hasError: false, error: undefined });
  }

  render(): ReactNode {
    // Always render children - this is a silent error boundary that only logs
    return this.props.children;
  }
}

